import { Dispatch, SetStateAction } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { UseFormClearErrors } from 'react-hook-form/dist/types/form';

import {
	EConfigurationDataFieldNames,
	IConfigurationDataFormValues,
	TConfigurationDataFieldNames,
} from '@interfaces/cmModule/configurationData';

import {
	EMavenirDataModelType,
	EMavenirDescriptorType,
	EMavenirDeviceType,
} from '@enums/configurationDataModelManagement';

import {
	configurationDataFormDefaultRequiredFields,
} from '@components/routes/CmModule/ConfigurationDataModelManagement/constants';

interface IUpdateFieldsParams {
	descriptorType?: string | null;
	deviceType?: string | null;
	dataModelType?: string | null;
	groupBy?: string | null;
	setValue: UseFormSetValue<IConfigurationDataFormValues>;
	clearErrors: UseFormClearErrors<IConfigurationDataFormValues>;
}

interface ISetRequiredFieldsParams {
	setRequiredFields: Dispatch<SetStateAction<Array<keyof IConfigurationDataFormValues>>>;
}

const updateForDescriptorAndDeviceType = ({
	descriptorType,
	deviceType,
	updatedFields,
	setValue,
	clearErrors,
}: IUpdateFieldsParams & { updatedFields: Array<keyof IConfigurationDataFormValues> }) => {
	if (descriptorType === EMavenirDescriptorType.modelWithoutDescriptor || deviceType === EMavenirDeviceType.Vnfc) {
		updatedFields.push(EConfigurationDataFieldNames.VDU_ID);
	} else {
		const index = updatedFields.indexOf(EConfigurationDataFieldNames.VDU_ID);

		if (index > -1) updatedFields.splice(index, 1);
		setValue(EConfigurationDataFieldNames.VDU_ID, null);
		clearErrors(EConfigurationDataFieldNames.VDU_ID);
	}
};

const updateForDataModelType = ({
	dataModelType,
	updatedFields,
	setValue,
	clearErrors,
}: IUpdateFieldsParams & { updatedFields: Array<keyof IConfigurationDataFormValues> }) => {
	if (dataModelType === EMavenirDataModelType.modelWithExistingSchema) {
		updatedFields.push(EConfigurationDataFieldNames.DATA_MODEL);
	} else {
		const index = updatedFields.indexOf(EConfigurationDataFieldNames.DATA_MODEL);

		if (index > -1) updatedFields.splice(index, 1);
		setValue(EConfigurationDataFieldNames.DATA_MODEL, '');
		clearErrors(EConfigurationDataFieldNames.DATA_MODEL);
	}
};

const updateForDeviceType = ({
	deviceType,
	updatedFields,
	setValue,
	clearErrors,
}: IUpdateFieldsParams & { updatedFields: Array<keyof IConfigurationDataFormValues> }) => {
	if (deviceType === EMavenirDeviceType.Cnf || deviceType === EMavenirDeviceType.Pnf) {
		const requiredFieldsDeviceType: Array<TConfigurationDataFieldNames> = [
			EConfigurationDataFieldNames.BUILD_VERSION,
		];

		const requiredFieldsDeviceTypeCnf: Array<TConfigurationDataFieldNames> = [
			EConfigurationDataFieldNames.ELEMENT_TYPE,
			EConfigurationDataFieldNames.GROUP_BY,
		];

		if (deviceType === EMavenirDeviceType.Cnf) {
			requiredFieldsDeviceType.push(...requiredFieldsDeviceTypeCnf);
		} else {
			requiredFieldsDeviceTypeCnf.forEach(field => {
				const index = updatedFields.indexOf(field);

				if (index > -1) updatedFields.splice(index, 1);
				setValue(field, null);
				clearErrors(field);
			});
		}

		if (deviceType === EMavenirDeviceType.Pnf) {
			requiredFieldsDeviceType.push(
				EConfigurationDataFieldNames.PNF_TYPE,
			);
		} else {
			const index = updatedFields.indexOf(EConfigurationDataFieldNames.PNF_TYPE);

			if (index > -1) updatedFields.splice(index, 1);
			setValue(EConfigurationDataFieldNames.PNF_TYPE, null);
			clearErrors(EConfigurationDataFieldNames.PNF_TYPE);
		}

		requiredFieldsDeviceType.forEach(field => {
			updatedFields.push(field);
		});
	} else {
		const index = updatedFields.indexOf(EConfigurationDataFieldNames.BUILD_VERSION);

		if (index > -1) updatedFields.splice(index, 1);
		setValue(EConfigurationDataFieldNames.BUILD_VERSION, null);
		clearErrors(EConfigurationDataFieldNames.BUILD_VERSION);
	}
};

const updateForGroupBy = ({
	groupBy,
	updatedFields,
	setValue,
	clearErrors,
}: IUpdateFieldsParams & { updatedFields: Array<keyof IConfigurationDataFormValues> }) => {
	if (groupBy) {
		updatedFields.push(EConfigurationDataFieldNames.PRODUCT_NAME);
	} else {
		const index = updatedFields.indexOf(EConfigurationDataFieldNames.PRODUCT_NAME);

		if (index > -1) updatedFields.splice(index, 1);
		setValue(EConfigurationDataFieldNames.PRODUCT_NAME, null);
		clearErrors(EConfigurationDataFieldNames.PRODUCT_NAME);
	}
};

const updateForDeviceTypeVnfc = ({
	deviceType,
	updatedFields,
	setValue,
	clearErrors,
}: IUpdateFieldsParams & {
	updatedFields: Array<keyof IConfigurationDataFormValues>;
}) => {
	const requiredFieldsDeviceTypeVnfc = [
		EConfigurationDataFieldNames.NS_ID,
		EConfigurationDataFieldNames.NS_VERSION,
		EConfigurationDataFieldNames.NF_TYPE,
		EConfigurationDataFieldNames.COMPONENT_TYPE,
		EConfigurationDataFieldNames.COMPONENT_VERSION,
	];

	if (deviceType === EMavenirDeviceType.Vnfc) {
		requiredFieldsDeviceTypeVnfc.forEach(field => {
			if (!updatedFields.includes(field)) updatedFields.push(field);
		});
	} else {
		requiredFieldsDeviceTypeVnfc.forEach(field => {
			const index = updatedFields.indexOf(field);

			if (index > -1) updatedFields.splice(index, 1);
			setValue(field, null);
			clearErrors(field);
		});
	}
};

export const updateRequiredFields = ({
	descriptorType,
	deviceType,
	dataModelType,
	groupBy,
	setValue,
	clearErrors,
	setRequiredFields,
}: IUpdateFieldsParams & ISetRequiredFieldsParams) => {
	const updatedFields = [...configurationDataFormDefaultRequiredFields];

	updateForDescriptorAndDeviceType({
		descriptorType,
		deviceType,
		updatedFields,
		setValue,
		clearErrors,
	});

	updateForDataModelType({
		dataModelType,
		updatedFields,
		setValue,
		clearErrors,
	});

	updateForDeviceType({
		deviceType,
		updatedFields,
		setValue,
		clearErrors,
	});

	updateForGroupBy({
		groupBy,
		updatedFields,
		setValue,
		clearErrors,
	});

	updateForDeviceTypeVnfc({
		deviceType,
		updatedFields,
		setValue,
		clearErrors,
	});

	setRequiredFields(updatedFields);
};
